import {
  Avatar,
  Button,
  Card,
  ChartCard,
  ContextPanel,
  DonutChart,
  HighlightCard,
  LineChart,
  PageHeader,
  ProfileCard,
  ProgressRing,
  StatStrip,
} from '@hemmafixa/ui';
import { formatOrgNumber, type ProviderStats } from '@hemmafixa/shared';
import {
  BadgeCheck,
  Building2,
  Clock,
  HandCoins,
  Mail,
  MessageSquare,
  Phone,
  Search,
  Trophy,
} from 'lucide-react';
import { Link } from 'react-router';
import { useCurrentUser } from '@/features/auth/api';
import { firstName } from '@/features/auth/access';
import { fill, useI18n } from '@/i18n/context';
import { linkTo } from '../shared/link-to';
import { OverviewTodos, RecentActivity } from '../shared/overview-parts';
import { QueryView } from '../shared/query-view';
import { countOf, useMonthPoints } from '../shared/stats-parts';
import { useProviderCharts, useProviderOverview, useProviderProfile } from './api';

/**
 * The provider's start page (P5-07, UI-06): the next step while the company is not verified, then numbers, charts
 * of the last six months, what needs them and what happened lately. The context panel holds the profile card.
 */
export function ProviderOverviewPage() {
  const user = useCurrentUser();
  const { t } = useI18n();
  const profile = useProviderProfile();
  const overview = useProviderOverview();
  const charts = useProviderCharts();
  const numbers = overview.data;
  const o = t.provider.overview;

  return (
    <div className="grid gap-4">
      <PageHeader title={fill(o.title, { name: firstName(user.name) })} description={o.description} />
      <QueryView query={profile} loading="cards">
        {(current) => {
          const status = current?.status ?? 'onboarding';
          const verified = status === 'verified';
          return (
            <>
              {!verified && <NextStep status={status} />}
              <StatStrip
                loading={overview.isPending}
                items={[
                  ...(verified
                    ? [
                        {
                          icon: Search,
                          tone: 'info' as const,
                          label: o.newJobs,
                          value: numbers?.newJobs ?? 0,
                          link: linkTo('/provider/jobs?proposal=none'),
                        },
                      ]
                    : []),
                  {
                    icon: HandCoins,
                    tone: 'brand' as const,
                    label: o.activeProposals,
                    value: numbers?.activeProposals ?? 0,
                    hint: fill(o.acceptedCount, { count: numbers?.acceptedProposals ?? 0 }),
                    progress: { value: numbers?.acceptedProposals ?? 0, max: numbers?.activeProposals ?? 0 },
                    link: linkTo('/provider/proposals'),
                  },
                  {
                    icon: MessageSquare,
                    tone: 'success' as const,
                    label: o.unreadMessages,
                    value: numbers?.unreadMessages ?? 0,
                    link: linkTo('/provider/messages'),
                  },
                  { label: o.won, value: numbers?.wonLast30Days ?? 0, icon: Trophy, tone: 'brand' as const },
                ]}
              />
              <ProposalCharts stats={charts.data} loading={charts.isPending} />
              <div className="grid items-start gap-4 lg:grid-cols-2">
                <OverviewTodos todos={numbers?.todo ?? []} area="provider" loading={overview.isPending} />
                <RecentActivity area="provider" />
              </div>
              <ContextPanel>
                <ProfileCard
                  avatar={<Avatar seed={user.id} name={user.name} size="xl" />}
                  name={user.name}
                  subtitle={current?.companyName ?? t.areas.provider}
                  details={[
                    { icon: Mail, label: t.settings.email, value: user.email },
                    ...(user.phone ? [{ icon: Phone, label: t.settings.phone, value: user.phone }] : []),
                    ...(current?.orgNumber
                      ? [
                          {
                            icon: Building2,
                            label: t.provider.onboarding.orgNumber,
                            value: formatOrgNumber(current.orgNumber),
                          },
                        ]
                      : []),
                  ]}
                  stats={[
                    { label: t.charts.active, value: numbers?.activeProposals ?? 0 },
                    { label: t.charts.won, value: numbers?.wonLast30Days ?? 0 },
                    { label: t.charts.unread, value: numbers?.unreadMessages ?? 0 },
                  ]}
                />
                <WinRate stats={charts.data} />
                {verified && <NextStep status={status} />}
              </ContextPanel>
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

function ProposalCharts({ stats, loading }: { stats?: ProviderStats; loading: boolean }) {
  const { t } = useI18n();
  const months = useMonthPoints();
  const c = t.charts;
  const statuses = t.status.proposal;
  const rows = stats?.proposalStatuses ?? [];
  const total = countOf(rows, ['submitted', 'accepted', 'chosen', 'declined', 'withdrawn', 'closed']);
  const sent = months(stats?.proposalsPerMonth ?? []);

  return (
    <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <ChartCard title={c.proposalsSentWon} description={c.lastSixMonths} loading={loading}>
        <LineChart
          title={c.proposalsSentWon}
          labels={sent}
          series={[
            { name: c.sent, color: 1, values: sent.map((point) => point.value) },
            { name: c.won, color: 2, values: (stats?.wonPerMonth ?? []).map((point) => point.value) },
          ]}
        />
      </ChartCard>
      <ChartCard title={c.yourProposals} loading={loading}>
        <DonutChart
          title={c.yourProposals}
          centerValue={total}
          centerLabel={c.proposals}
          segments={[
            { label: statuses.submitted, value: countOf(rows, ['submitted']), color: 1 },
            { label: statuses.accepted, value: countOf(rows, ['accepted']), color: 2 },
            { label: statuses.chosen, value: countOf(rows, ['chosen']), color: 3 },
            { label: c.other, value: countOf(rows, ['declined', 'withdrawn', 'closed']), color: 'muted' },
          ]}
        />
      </ChartCard>
    </div>
  );
}

/** Won against everything that got an answer; shown once there is something to compare */
function WinRate({ stats }: { stats?: ProviderStats }) {
  const { t } = useI18n();
  const rows = stats?.proposalStatuses ?? [];
  const won = countOf(rows, ['chosen']);
  const total = countOf(rows, ['submitted', 'accepted', 'chosen', 'declined', 'withdrawn', 'closed']);
  if (total === 0) return null;
  return (
    <Card className="flex items-center gap-4 bg-muted p-4 shadow-none">
      <ProgressRing label={t.charts.winRate} value={won} max={total} size="sm" color={3} />
      <div className="grid gap-0.5">
        <p className="text-sm font-semibold">{t.charts.winRate}</p>
        <p className="text-xs text-muted-foreground">{fill(t.charts.winRateText, { won, total })}</p>
      </div>
    </Card>
  );
}

/** Where the company profile stands: a call to action until verified, a link to the public page after. */
function NextStep({ status }: { status: string }) {
  const { t } = useI18n();
  const o = t.provider.overview;
  const s = t.provider.onboarding;
  const step =
    status === 'verified'
      ? {
          icon: BadgeCheck,
          title: s.verifiedTitle,
          text: s.verifiedText,
          action: s.viewPublic,
          to: '/provider/onboarding/public',
        }
      : status === 'pending'
        ? {
            icon: Clock,
            title: s.pendingTitle,
            text: s.pendingText,
            action: o.viewProfile,
            to: '/provider/onboarding',
          }
        : {
            icon: Building2,
            title: status === 'rejected' ? s.rejectedTitle : o.onboardingTitle,
            text: status === 'rejected' ? s.rejectedText : o.onboardingText,
            action: o.onboardingAction,
            to: '/provider/onboarding',
          };
  return (
    <HighlightCard
      icon={step.icon}
      title={step.title}
      action={
        <Button asChild size="sm">
          <Link to={step.to}>{step.action}</Link>
        </Button>
      }
    >
      {step.text}
    </HighlightCard>
  );
}
